"use client";

import { useRef, useEffect } from "react";
import Link from "next/link";
import { Send, Loader2, ExternalLink, X } from "lucide-react";
import { TonyAvatar } from "@/components/ai/TonyAvatar";
import {
  QUICK_REPLIES,
  renderMessageContent,
} from "./support-chat-utils";
import { useSupportChat } from "./useSupportChat";
import { APP_CONFIG } from "@/data/constants";

type SupportChatPanelProps = {
  variant?: "inline" | "floating";
  autoInit?: boolean;
  onClose?: () => void;
  className?: string;
};

export function SupportChatPanel({
  variant = "inline",
  autoInit = false,
  onClose,
  className = "",
}: SupportChatPanelProps) {
  const {
    messages,
    input,
    setInput,
    loading,
    initialized,
    initChat,
    sendMessage,
  } = useSupportChat(autoInit);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (variant === "floating" && initialized) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [variant, initialized]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleFocus = () => {
    if (!initialized) initChat();
  };

  const isFloating = variant === "floating";
  const showQuickReplies =
    initialized && messages.length <= 1 && !loading;

  return (
    <div
      className={`flex flex-col bg-white overflow-hidden ${
        isFloating
          ? "h-full"
          : "rounded-2xl border border-purple-100 shadow-xl shadow-purple-900/10"
      } ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#280a49] to-purple-800 text-white shrink-0">
        <TonyAvatar size="sm" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">토니 AI 상담</p>
          <p className="text-xs text-purple-200 truncate">
            24시간 1:1 챗봇 문의
          </p>
        </div>
        {isFloating && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="채팅 닫기"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className={`overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 ${
          isFloating ? "flex-1" : "h-[320px] sm:h-[360px]"
        }`}
      >
        {!initialized ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8">
            <TonyAvatar size="md" />
            <p className="mt-4 text-sm text-gray-600 leading-relaxed">
              궁금한 점을 입력하거나
              <br />
              아래 빠른 질문을 눌러보세요
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {QUICK_REPLIES.map(q => (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => sendMessage(q.message)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors"
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
              >
                {msg.role === "assistant" && (
                  <div className="shrink-0 mt-0.5">
                    <TonyAvatar size="sm" />
                  </div>
                )}
                <div
                  className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[#280a49] text-white rounded-tr-sm"
                      : "bg-white text-gray-800 border border-gray-100 shadow-sm rounded-tl-sm"
                  }`}
                >
                  {msg.role === "assistant"
                    ? renderMessageContent(msg.content)
                    : msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2">
                <TonyAvatar size="sm" variant="thinking" />
                <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick replies after welcome */}
      {showQuickReplies && (
        <div className="px-3 py-2 flex flex-wrap gap-1.5 bg-gray-50 border-t border-gray-100 shrink-0">
          {QUICK_REPLIES.map(q => (
            <button
              key={q.label}
              type="button"
              onClick={() => sendMessage(q.message)}
              className="text-xs px-2.5 py-1.5 rounded-full bg-white border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors"
            >
              {q.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="shrink-0 border-t border-gray-200 bg-white">
        <form onSubmit={handleSubmit} className="flex items-end gap-2 p-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder="무엇이 궁금하신가요?"
            rows={1}
            disabled={loading}
            className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-400 max-h-24 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="p-2.5 rounded-xl bg-[#280a49] text-white hover:bg-purple-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            aria-label="전송"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        <div className="px-3 pb-3">
          <Link
            href="/contact"
            className="flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-purple-700 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            1:1 문의하기
          </Link>
          <a
            href={`mailto:${APP_CONFIG.SUPPORT_EMAIL}`}
            className="flex items-center justify-center gap-1.5 text-xs text-gray-400 hover:text-purple-700 transition-colors mt-1"
          >
            {APP_CONFIG.SUPPORT_EMAIL}
          </a>
        </div>
      </div>
    </div>
  );
}
